import { useMemo, useState } from 'react';
import { Download, Search, HardDrive } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useStorageBusiness, type StorageBucketUsageRow } from '@/api/s3';
import { formatNum } from '@/pages/services/_shared/analytics-format';
import { formatGiBExact } from '../storage-format';
import { RefreshButton } from '@/pages/services/_shared/AnalyticsKit';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import BusinessUsageShell from '../../usage/BusinessUsageShell';

// ---- Month period selection ----------------------------------------------
// Wasabi bills on stored volume (GiB-month, binary 1024³), reconciled per calendar month. The
// console retains a bounded daily history, so the picker offers the trailing 12
// months; each maps to an absolute [from,to] the backend folds server-side.
interface MonthOpt {
  key: string; // YYYY-MM
  label: string; // "Jul 2026"
}
function buildMonths(): MonthOpt[] {
  const now = new Date();
  const out: MonthOpt[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    out.push({ key, label });
  }
  return out;
}
function monthToRange(key: string): { from: string; to: string } {
  const [y, m] = key.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1));
  const nextMonth = new Date(Date.UTC(y, m, 1));
  const now = new Date();
  const to = nextMonth.getTime() > now.getTime() ? now : nextMonth;
  return { from: start.toISOString(), to: to.toISOString() };
}

// ---- CSV -----------------------------------------------------------------
function downloadCsv(rows: StorageBucketUsageRow[], periodSlug: string) {
  const header = 'Bucket,Avg stored (bytes),Peak stored (bytes),Billable objects,Deleted (bytes)';
  const body = rows
    .map((r) => `${r.bucket},${r.avg_stored_bytes},${r.peak_stored_bytes},${r.billable_objects},${r.deleted_bytes}`)
    .join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `object-storage-usage-${periodSlug}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function bucketColumns(): ColumnDef<StorageBucketUsageRow, unknown>[] {
  return [
    {
      id: 'bucket',
      accessorKey: 'bucket',
      header: 'Bucket',
      cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.bucket}</span>,
    },
    {
      // Company policy bills the peak — show it first as the billed figure.
      id: 'peak_stored_bytes',
      header: 'Peak stored (billed)',
      size: 160,
      cell: ({ row }) => formatGiBExact(row.original.peak_stored_bytes),
    },
    {
      id: 'avg_stored_bytes',
      header: 'Avg stored',
      size: 140,
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)' }}>{formatGiBExact(row.original.avg_stored_bytes)}</span>
      ),
    },
    {
      id: 'billable_objects',
      header: 'Objects',
      size: 120,
      cell: ({ row }) => formatNum(row.original.billable_objects),
    },
    {
      id: 'deleted_bytes',
      header: 'Deleted',
      size: 120,
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)' }}>{formatGiBExact(row.original.deleted_bytes)}</span>
      ),
    },
  ];
}

/**
 * Business Usage — storage reconciliation ("đối soát") for one Object Storage
 * service. Shows the billing-basis stored volume (avg GiB-month + peak) plus a
 * per-bucket breakdown for a selected month, sourced live from the Wasabi
 * console. Mirrors the CDN Business Usage layout, adapted to storage's at-rest
 * billing model (no traffic/geo dimensions).
 */
export function BusinessUsagePage({ serviceId }: { serviceId: string }) {
  const months = useMemo(buildMonths, []);
  const [month, setMonth] = useState(months[0].key);
  const [search, setSearch] = useState('');
  const range = useMemo(() => monthToRange(month), [month]);
  const periodLabel = months.find((m) => m.key === month)?.label ?? month;

  const { data, isLoading, refresh } = useStorageBusiness(serviceId, range);
  const summary = data?.summary;
  const columns = useMemo(bucketColumns, []);

  const buckets = data?.buckets ?? [];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? buckets.filter((b) => b.bucket.toLowerCase().includes(q)) : buckets;
  }, [buckets, search]);

  const kpis = [
    {
      // Company policy bills the peak stored volume in the period.
      label: 'Peak stored (billed)',
      value: formatGiBExact(summary?.peak_stored_bytes ?? 0),
      hint: `${formatNum(Math.round(summary?.gib_month ?? 0))} GiB billed`,
    },
    { label: 'Avg stored', value: formatGiBExact(summary?.avg_stored_bytes ?? 0) },
    { label: 'Billable objects', value: formatNum(summary?.billable_objects ?? 0) },
    { label: 'Buckets', value: formatNum(buckets.length) },
  ];

  return (
    <BusinessUsageShell
      icon={<HardDrive size={22} />}
      breadcrumbs={<S3ConsoleBreadcrumbs trail={[{ label: 'Business Usage' }]} />}
      description="Billed storage volume for reconciliation — averaged GiB-month plus a per-bucket breakdown."
      basis="Billed on the peak stored volume (padded storage) reached in the selected period; the average is shown for reference."
      period={
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger size="md" style={{ width: 170, flexShrink: 0 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m.key} value={m.key}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
      actions={
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <RefreshButton onRefresh={refresh} />
          <Button
            variant="secondary"
            size="md"
            startIcon={<Download size={14} />}
            disabled={buckets.length === 0}
            onClick={() => downloadCsv(rows, month)}
          >
            Export CSV
          </Button>
        </div>
      }
      kpis={kpis}
      kpisLoading={isLoading}
      periodLabel={periodLabel}
      search={
        <Input
          inputSize="md"
          placeholder="Filter buckets"
          startSlot={<Search />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260, flexShrink: 0 }}
        />
      }
    >
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(r) => r.bucket}
        emptyState={
          <EmptyState
            icon={<HardDrive size={28} />}
            title="No usage in this period"
            description="No billed storage recorded for the selected month."
          />
        }
      />
    </BusinessUsageShell>
  );
}

export default BusinessUsagePage;
